import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Button, Dialog, Field, Input, Textarea } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { DocumentPreviews } from '../../components/DocumentPreviews.jsx';

/**
 * What each decision does, in the words the dialog needs.
 *
 * Kept as data rather than as branches in the JSX: the decisions differ only
 * in their copy, and a table makes it obvious that none of them is missing its
 * warning. The list and the detail screen both drive the same dialog from it.
 */
export const DECISIONS = {
  /*
   * Not a moderation decision like the others — it goes to a different
   * endpoint — but it belongs in the same dialog: an admin reaching for it
   * is in the same place, weighing the same agency, and should get the same
   * "say why" prompt and the same audit trail.
   */
  delete: {
    title: (broker) => `Delete ${broker.businessName}?`,
    warning:
      'The agency is struck off and drops out of the directory owners hire from, and off every list here. Its leads, enquiries and payments stay, because those records point at it. The person behind it keeps their login — delete the account itself if that is what you meant. Not reversible from the panel.',
    confirm: 'Delete agency',
    variant: 'danger',
    needsReason: true,
    hint: 'Required. Recorded in the audit log against your account.',
    success: (broker) => `${broker.businessName} has been deleted.`,
  },
  approve: {
    title: (broker) => `Verify ${broker.businessName}?`,
    warning:
      'The teal Verified badge appears against every one of their listings. It says Grihvaas checked the agency’s registration — check it before approving.',
    confirm: 'Verify broker',
    variant: 'primary',
    needsReason: false,
    hint: 'Optional. Recorded in the audit log against your account.',
    success: (broker) => `${broker.businessName} is verified.`,
  },
  reject: {
    title: (broker) => `Reject ${broker.businessName}?`,
    warning:
      'They lose the Verified badge and see the reason you give here. They can submit their documents again.',
    confirm: 'Reject verification',
    variant: 'danger',
    needsReason: true,
    hint: 'The broker sees this. Recorded in the audit log against your account.',
    success: (broker) => `${broker.businessName} has been rejected.`,
  },
  suspend: {
    title: (broker) => `Suspend ${broker.businessName}?`,
    warning:
      'Their listings stop being shown and they cannot take new leads. Tenants already in conversation with them are not told. This is reversible.',
    confirm: 'Suspend broker',
    variant: 'danger',
    needsReason: true,
    hint: 'Recorded in the audit log against your account.',
    success: (broker) => `${broker.businessName} has been suspended.`,
  },
  reinstate: {
    title: (broker) => `Reinstate ${broker.businessName}?`,
    warning:
      'Their listings are shown again and they can take leads from today. The suspension stays in the audit log.',
    confirm: 'Reinstate broker',
    variant: 'primary',
    needsReason: true,
    hint: 'Recorded in the audit log against your account.',
    success: (broker) => `${broker.businessName} is active again.`,
  },
};

/**
 * The moderation dialog and the state behind it.
 *
 * Returns the element to render plus an `ask` that opens it, so a screen only
 * has to say which broker and which decision. Both query keys are invalidated
 * on success because a decision taken on the list changes the detail screen
 * and the other way round.
 */
export function useBrokerModeration({ onDeleted } = {}) {
  const queryClient = useQueryClient();

  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [attached, setAttached] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  const isApproval = pending?.decision === 'approve';
  /* What the agency itself sent in, if anything. */
  const documents = pending?.broker?.verification?.documents || [];

  const decision = pending ? DECISIONS[pending.decision] : null;

  const close = () => {
    setPending(null);
    setReason('');
    setRegistrationNumber('');
    setAttached([]);
  };

  /**
   * Puts a document in the agency's file.
   *
   * Three steps, the same as everywhere else in this system: ask for a slot,
   * send the bytes straight to storage, then confirm. Only a confirmed asset
   * is attachable, so a half-finished upload cannot end up cited as evidence
   * for a verification.
   */
  const upload = async (file) => {
    if (!file) return;
    setIsUploading(true);

    try {
      const slot = await api.broker.documentSlot(pending.broker._id, {
        fileName: file.name,
        mimeType: file.type,
        byteSize: file.size,
      });

      const put = await fetch(slot.uploadUrl, {
        method: slot.method || 'PUT',
        headers: slot.headers || { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error('The file could not be uploaded. Try again.');

      await api.broker.documentComplete(pending.broker._id, slot.assetId);
      setAttached((current) => [...current, { assetId: slot.assetId, fileName: file.name }]);
      toast.success(`${file.name} attached.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const confirm = async () => {
    setIsWorking(true);

    try {
      if (pending.decision === 'delete') {
        await api.broker.removeBroker(pending.broker._id, reason.trim());
      } else {
        await api.broker.moderateBroker(pending.broker._id, {
          decision: pending.decision,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          // Recorded with the approval, so the badge and the evidence for it
          // are written down together.
          ...(isApproval && registrationNumber.trim()
            ? { registrationNumber: registrationNumber.trim() }
            : {}),
          ...(isApproval && attached.length
            ? { documentIds: attached.map((doc) => doc.assetId) }
            : {}),
        });
      }

      toast.success(decision.success(pending.broker));
      const wasDeleted = pending.decision === 'delete';
      close();
      queryClient.invalidateQueries({ queryKey: ['admin-brokers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-broker', pending.broker._id] });
      // The detail page it was opened from is now showing a record that has
      // gone from every list; it decides where to send the admin next.
      if (wasDeleted) onDeleted?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  const dialog = (
    <Dialog
      open={Boolean(pending)}
      onClose={close}
      title={pending ? decision.title(pending.broker) : ''}
      warning={decision?.warning}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={decision?.variant}
            onClick={confirm}
            loading={isWorking}
            disabled={Boolean(decision?.needsReason) && reason.trim().length < 3}
          >
            {decision?.confirm}
          </Button>
        </>
      }
    >
      <div className="stack">
        {isApproval ? (
          <>
            <Field
              label="RERA registration"
              hint="What you checked. Saved against the agency, replacing whatever it had."
              id="verify-rera"
            >
              <Input
                id="verify-rera"
                value={registrationNumber}
                onChange={(event) => setRegistrationNumber(event.target.value)}
                placeholder={pending?.broker?.registrationNumber || 'CG/RAI/2026/0001'}
              />
            </Field>

            {/* The evidence the agency sent, openable before deciding — a
                count alone tells an admin nothing they can check. */}
            <Field
              label="Documents on file"
              hint="Anything you attach is filed under the agency, with you recorded as the one who sent it."
            >
              {documents.length ? (
                <DocumentPreviews documents={documents} />
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>
                  This agency has not sent any. Attach what you checked, or say what it was
                  in the reason below.
                </p>
              )}

              {attached.length ? (
                <ul className="stack stack--sm" style={{ marginTop: 'var(--space-2)' }}>
                  {attached.map((doc) => (
                    <li key={doc.assetId}>{`${doc.fileName} — attached`}</li>
                  ))}
                </ul>
              ) : null}

              <input
                type="file"
                /* Exactly what the server allows for this purpose — offering
                   a type it refuses is a rejection after the file is picked. */
                accept="image/jpeg,image/png,application/pdf"
                disabled={isUploading}
                onChange={(event) => {
                  const [file] = event.target.files || [];
                  // Cleared so the same file can be picked again after a
                  // failed upload; without this the input ignores it.
                  event.target.value = '';
                  upload(file);
                }}
                style={{ marginTop: 'var(--space-2)' }}
              />
            </Field>
          </>
        ) : null}

        <Field label="Reason" hint={decision?.hint}>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="What is the reason for this decision?"
          />
        </Field>
      </div>
    </Dialog>
  );

  return {
    dialog,
    ask: (broker, name) => setPending({ broker, decision: name }),
  };
}
